// src/components/email/EmailCampaignCreate.jsx
// ═══════════════════════════════════════════════════════════════════════════
// 📧 CREAR CAMPAÑA DE EMAIL MARKETING - CON SELECCIÓN DE GRUPO/CSV
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';
import UploadCSV from './UploadCSV';  // ← Componente modal de upload

const EmailCampaignCreate = () => {
  const navigate = useNavigate();
  
  // Datos de la campaña
  const [nombre, setNombre] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  
  // ← ← ← NUEVO: Tipo de destinatarios y selección
  const [tipoDestinatarios, setTipoDestinatarios] = useState('grupo'); // 'grupo' | 'csv' | 'todos'
  const [grupos, setGrupos] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState('');
  const [csvData, setCsvData] = useState(null);
  
  // Estados de carga
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  
  // Modal CSV
  const [showUploadCSV, setShowUploadCSV] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      // Cargar plantillas
      const templatesRes = await api.get('/email/plantillas');
      setPlantillas(templatesRes.data.plantillas || []);

      // Cargar grupos
      const gruposRes = await api.get('/email/grupos');
      setGrupos(gruposRes.data.grupos || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos'
      });
    }
  };

  const handleCSVUploadComplete = (data) => {
    setCsvData(data);
    setShowUploadCSV(false);
    
    Swal.fire({
      icon: 'success',
      title: '✅ CSV Listo',
      html: `
        <p><strong>Emails válidos:</strong> ${data.emails_validos}</p>
        <p><strong>Archivo:</strong> ${data.archivo}</p>
      `,
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleEnviar = async () => {
    // Validaciones
    if (!nombre || !templateId) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos Requeridos',
        text: 'Completa el nombre de la campaña y selecciona una plantilla'
      });
      return;
    }

    if (tipoDestinatarios === 'grupo' && !grupoSeleccionado) {
      Swal.fire({
        icon: 'warning',
        title: 'Selecciona un Grupo',
        text: 'Debes seleccionar un grupo de destinatarios'
      });
      return;
    }

    if (tipoDestinatarios === 'csv' && !csvData) {
      Swal.fire({
        icon: 'warning',
        title: 'Sube un CSV',
        text: 'Debes subir un archivo CSV con los destinatarios'
      });
      return;
    }

    // Confirmación
    const destinatariosInfo = {
      grupo: grupos.find(g => g.id === parseInt(grupoSeleccionado))?.nombre,
      csv: csvData?.archivo,
      todos: 'Todos los clientes activos'
    };

    const result = await Swal.fire({
      icon: 'question',
      title: '¿Iniciar Campaña?',
      html: `
        <p><strong>Campaña:</strong> ${nombre}</p>
        <p><strong>Destinatarios:</strong> ${destinatariosInfo[tipoDestinatarios] || 'N/A'}</p>
        ${tipoDestinatarios === 'csv' ? `<p style="color: #64748b; font-size: 12px;">📊 ${csvData?.emails_validos} emails válidos</p>` : ''}
      `,
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#64748b',
      confirmButtonText: '🚀 Sí, Iniciar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setEnviando(true);
    
    try {
      // Preparar payload según tipo de destinatarios
      const payload = {
        nombre,
        templateId,
        tipo_destinatarios: tipoDestinatarios
      };

      if (tipoDestinatarios === 'grupo') {
        payload.EmailGrupoId = parseInt(grupoSeleccionado);
      } else if (tipoDestinatarios === 'csv') {
        payload.archivo_csv = csvData.archivo;
      }
      // Si es 'todos', no se necesita campo adicional

      // Enviar campaña
      const res = await api.post('/email/campanas', payload);
      setProgreso(res.data.campana);

      // Polling para progreso en tiempo real
      const interval = setInterval(async () => {
        try {
          const status = await api.get(`/email/campanas/${res.data.campana.id}`);
          setProgreso(status.data.campana);

          if (status.data.campana.estado === 'completado') {
            clearInterval(interval);
            
            Swal.fire({
              icon: 'success',
              title: '✅ Campaña Completada',
              html: `
                <p><strong>Enviados:</strong> ${status.data.campana.total_enviados}</p>
                <p><strong>Fallidos:</strong> ${status.data.campana.total_fallidos}</p>
              `,
              timer: 3000,
              showConfirmButton: false
            });

            setTimeout(() => navigate('/email/campanas'), 3000);
          }
        } catch (error) {
          console.error('Error verificando progreso:', error);
        }
      }, 3000);

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo iniciar la campaña'
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📧 Nueva Campaña de Email</h1>
          <p style={styles.subtitle}>Configura y envía tu campaña masiva</p>
        </div>
        <button onClick={() => navigate('/email/campanas')} style={styles.btnSecondary}>
          ← Volver
        </button>
      </div>

      {/* Formulario Principal */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Configuración de la Campaña</h2>

        {/* Nombre de la Campaña */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre de Campaña *</label>
          <input
            type="text"
            style={styles.input}
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            placeholder="Ej: Promoción Marzo 2026"
            disabled={enviando}
          />
        </div>

        {/* Plantilla */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Plantilla de Email *</label>
          <select
            style={styles.select}
            value={templateId}
            onChange={e => setTemplateId(e.target.value)}
            disabled={enviando}
          >
            <option value="">Seleccionar plantilla...</option>
            {plantillas.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </div>

        {/* ← ← ← SELECCIÓN DE DESTINATARIOS */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Destinatarios *</label>
          
          {/* Tabs de selección */}
          <div style={styles.destTabs}>
            <button
              onClick={() => setTipoDestinatarios('grupo')}
              style={{
                ...styles.destTab,
                ...(tipoDestinatarios === 'grupo' ? styles.destTabActive : {})
              }}
            >
              👥 Grupo
            </button>
            <button
              onClick={() => setTipoDestinatarios('csv')}
              style={{
                ...styles.destTab,
                ...(tipoDestinatarios === 'csv' ? styles.destTabActive : {})
              }}
            >
              📎 CSV
            </button>
            <button
              onClick={() => setTipoDestinatarios('todos')}
              style={{
                ...styles.destTab,
                ...(tipoDestinatarios === 'todos' ? styles.destTabActive : {})
              }}
            >
              🌐 Todos los Clientes
            </button>
          </div>

          {/* Contenido según selección */}
          <div style={styles.destContent}>
            
            {/* Opción: Grupo */}
            {tipoDestinatarios === 'grupo' && (
              <div style={styles.grupoSelector}>
                <select
                  style={styles.select}
                  value={grupoSeleccionado}
                  onChange={e => setGrupoSeleccionado(e.target.value)}
                  disabled={enviando}
                >
                  <option value="">Seleccionar grupo...</option>
                  {grupos.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.nombre} ({g.total_miembros || 0} miembros)
                    </option>
                  ))}
                </select>
                {grupoSeleccionado && (
                  <p style={styles.grupoInfo}>
                    📊 {grupos.find(g => g.id === parseInt(grupoSeleccionado))?.total_miembros || 0} destinatarios
                  </p>
                )}
              </div>
            )}

            {/* Opción: CSV */}
            {tipoDestinatarios === 'csv' && (
              <div style={styles.csvSelector}>
                {!csvData ? (
                  <button
                    onClick={() => setShowUploadCSV(true)}
                    style={styles.btnUpload}
                  >
                    📎 Subir Archivo CSV
                  </button>
                ) : (
                  <div style={styles.csvInfo}>
                    <p><strong>✅ Archivo:</strong> {csvData.archivo}</p>
                    <p><strong>📧 Emails válidos:</strong> {csvData.emails_validos}</p>
                    <p><strong>❌ Emails inválidos:</strong> {csvData.emails_invalidos}</p>
                    <button
                      onClick={() => { setCsvData(null); setShowUploadCSV(true); }}
                      style={styles.btnChangeCSV}
                    >
                      🔄 Cambiar archivo
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Opción: Todos los Clientes */}
            {tipoDestinatarios === 'todos' && (
              <div style={styles.todosInfo}>
                <p>📧 Se enviará a <strong>todos los clientes activos</strong> registrados en el sistema.</p>
                <p style={styles.warning}>⚠️ Asegúrate de tener consentimiento para enviar emails masivos.</p>
              </div>
            )}
          </div>
        </div>

        {/* Botón de Enviar */}
        <div style={styles.actions}>
          <button
            onClick={handleEnviar}
            disabled={enviando || !nombre || !templateId || 
              (tipoDestinatarios === 'grupo' && !grupoSeleccionado) ||
              (tipoDestinatarios === 'csv' && !csvData)}
            style={{
              ...styles.btnPrimary,
              opacity: (enviando || !nombre || !templateId || 
                (tipoDestinatarios === 'grupo' && !grupoSeleccionado) ||
                (tipoDestinatarios === 'csv' && !csvData)) ? 0.6 : 1,
              cursor: (enviando || !nombre || !templateId || 
                (tipoDestinatarios === 'grupo' && !grupoSeleccionado) ||
                (tipoDestinatarios === 'csv' && !csvData)) ? 'not-allowed' : 'pointer'
            }}
          >
            {enviando ? '⏳ Iniciando...' : '🚀 Iniciar Campaña'}
          </button>
        </div>

        {/* Progreso en tiempo real */}
        {progreso && (
          <div style={styles.progressSection}>
            <h3 style={styles.progressTitle}>📊 Progreso de Envío</h3>
            <div style={styles.progressBar}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${progreso.total_destinatarios > 0 ? (progreso.total_enviados / progreso.total_destinatarios) * 100 : 0}%`
                }}
              />
            </div>
            <div style={styles.progressStats}>
              <div style={styles.progressStat}>
                <span style={styles.progressStatLabel}>Total</span>
                <span style={styles.progressStatValue}>{progreso.total_destinatarios}</span>
              </div>
              <div style={styles.progressStat}>
                <span style={styles.progressStatLabel}>Enviados</span>
                <span style={{ ...styles.progressStatValue, color: '#10b981' }}>{progreso.total_enviados}</span>
              </div>
              <div style={styles.progressStat}>
                <span style={styles.progressStatLabel}>Fallidos</span>
                <span style={{ ...styles.progressStatValue, color: '#ef4444' }}>{progreso.total_fallidos}</span>
              </div>
            </div>
            {progreso.estado === 'completado' && (
              <p style={styles.successMessage}>✅ Campaña completada exitosamente</p>
            )}
          </div>
        )}
      </div>

      {/* Modal de Upload CSV */}
      {showUploadCSV && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <UploadCSV
              onUploadComplete={handleCSVUploadComplete}
              onCancel={() => setShowUploadCSV(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: 'calc(100vh - 70px)',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    padding: '24px 32px',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnSecondary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxWidth: '800px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb'
  },
  formGroup: {
    marginBottom: '24px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    background: 'white',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    background: 'white',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  // ← ← ← ESTILOS PARA SELECCIÓN DE DESTINATARIOS
  destTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px'
  },
  destTab: {
    flex: 1,
    padding: '12px 16px',
    background: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  destTabActive: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderColor: '#3b82f6'
  },
  destContent: {
    padding: '16px',
    background: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  grupoSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  grupoInfo: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },
  csvSelector: {
    textAlign: 'center'
  },
  btnUpload: {
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  csvInfo: {
    textAlign: 'left',
    padding: '12px',
    background: 'white',
    borderRadius: '8px',
    fontSize: '13px'
  },
  btnChangeCSV: {
    marginTop: '12px',
    padding: '8px 16px',
    background: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  todosInfo: {
    textAlign: 'center',
    color: '#64748b'
  },
  warning: {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '8px'
  },
  actions: {
    paddingTop: '20px'
  },
  btnPrimary: {
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  progressSection: {
    marginTop: '24px',
    padding: '20px',
    background: '#f9fafb',
    borderRadius: '8px',
    border: '2px solid #e5e7eb'
  },
  progressTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '16px'
  },
  progressBar: {
    width: '100%',
    backgroundColor: '#e5e7eb',
    borderRadius: '9999px',
    height: '12px',
    marginBottom: '16px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
    borderRadius: '9999px',
    transition: 'width 0.3s'
  },
  progressStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px'
  },
  progressStat: {
    padding: '12px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  progressStatLabel: {
    display: 'block',
    fontSize: '11px',
    color: '#64748b',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '4px'
  },
  progressStatValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b'
  },
  successMessage: {
    marginTop: '16px',
    padding: '12px',
    background: '#d1fae5',
    borderRadius: '8px',
    textAlign: 'center',
    color: '#065f46',
    fontWeight: '600'
  },
  // ← ← ← ESTILOS DEL MODAL CSV
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto'
  }
};

export default EmailCampaignCreate;